import { useState } from 'react';

interface MerchandiseItem {
  id: string;
  name: string;
  nameEn?: string;
  description?: string;
  price: number;
  imageUrl?: string;
  isBundle?: boolean;
  bundleBadge?: string;
  maxQuantity?: number;
}

interface MerchandiseSettingsData {
  title?: string;
  subtitle?: string;
  headerImageUrl?: string;
  allowMultiple?: boolean;
  items?: MerchandiseItem[];
}

interface Props {
  settings: MerchandiseSettingsData;
  onChange: (settings: MerchandiseSettingsData) => void;
}

export default function MerchandiseSettings({ settings, onChange }: Props) {
  const [activeSection, setActiveSection] = useState<'items' | 'display'>('items');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const items = settings.items || [];

  function addItem() {
    const newItem: MerchandiseItem = {
      id: `item_${Date.now()}`,
      name: '新商品',
      price: 0,
    };
    onChange({ ...settings, items: [...items, newItem] });
    setExpandedItem(newItem.id);
  }

  function updateItem(id: string, updates: Partial<MerchandiseItem>) {
    onChange({
      ...settings,
      items: items.map(item => item.id === id ? { ...item, ...updates } : item),
    });
  }

  function removeItem(id: string) {
    onChange({
      ...settings,
      items: items.filter(item => item.id !== id),
    });
    if (expandedItem === id) {
      setExpandedItem(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
        <button
          onClick={() => setActiveSection('items')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeSection === 'items' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          商品 ({items.length})
        </button>
        <button
          onClick={() => setActiveSection('display')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeSection === 'display' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          顯示設定
        </button>
      </div>

      {/* Items Section */}
      {activeSection === 'items' && (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Item Header */}
              <div
                className="px-3 py-2 bg-gray-50 flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">#{idx + 1}</span>
                  <span className="font-medium text-sm">{item.name}</span>
                  <span className="text-xs text-gray-500">${item.price}</span>
                  {item.isBundle && (
                    <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">
                      {item.bundleBadge || '組合'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(item.id);
                    }}
                    className="text-red-500 hover:text-red-600 text-xs"
                  >
                    刪除
                  </button>
                  <span className="text-gray-400">{expandedItem === item.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Item Details (Expanded) */}
              {expandedItem === item.id && (
                <div className="p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">商品名稱</label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(item.id, { name: e.target.value })}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">英文名稱</label>
                      <input
                        type="text"
                        value={item.nameEn || ''}
                        onChange={(e) => updateItem(item.id, { nameEn: e.target.value })}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">價格</label>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => updateItem(item.id, { price: Number(e.target.value) })}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">最大數量</label>
                      <input
                        type="number"
                        value={item.maxQuantity || ''}
                        onChange={(e) => updateItem(item.id, { maxQuantity: e.target.value ? Number(e.target.value) : undefined })}
                        placeholder="不限"
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">說明</label>
                    <textarea
                      value={item.description || ''}
                      onChange={(e) => updateItem(item.id, { description: e.target.value })}
                      placeholder="商品描述"
                      rows={2}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">圖片網址 (建議 600x600)</label>
                    <input
                      type="url"
                      value={item.imageUrl || ''}
                      onChange={(e) => updateItem(item.id, { imageUrl: e.target.value })}
                      placeholder="https://example.com/product.jpg"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                    />
                  </div>

                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <span className="text-sm">標記為組合商品</span>
                    </div>
                    <button
                      onClick={() => updateItem(item.id, { isBundle: !item.isBundle })}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        item.isBundle ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        item.isBundle ? 'left-5' : 'left-0.5'
                      }`} />
                    </button>
                  </div>

                  {item.isBundle && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">組合標籤</label>
                      <input
                        type="text"
                        value={item.bundleBadge || ''}
                        onChange={(e) => updateItem(item.id, { bundleBadge: e.target.value })}
                        placeholder="例: 超值組合"
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          <button
            onClick={addItem}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors"
          >
            + 新增商品
          </button>
        </div>
      )}

      {/* Display Section */}
      {activeSection === 'display' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">區塊標題</label>
            <input
              type="text"
              value={settings.title || ''}
              onChange={(e) => onChange({ ...settings, title: e.target.value })}
              placeholder="例: 加購商品"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">副標題</label>
            <input
              type="text"
              value={settings.subtitle || ''}
              onChange={(e) => onChange({ ...settings, subtitle: e.target.value })}
              placeholder="例: 限量優惠"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">標題圖片 (建議 800x200)</label>
            <input
              type="url"
              value={settings.headerImageUrl || ''}
              onChange={(e) => onChange({ ...settings, headerImageUrl: e.target.value })}
              placeholder="https://example.com/header.jpg"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <span className="text-sm font-medium">允許多選</span>
              <p className="text-xs text-gray-500 mt-0.5">用戶可選擇多個商品</p>
            </div>
            <button
              onClick={() => onChange({ ...settings, allowMultiple: !settings.allowMultiple })}
              className={`w-10 h-5 rounded-full transition-colors relative ${
                settings.allowMultiple ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                settings.allowMultiple ? 'left-5' : 'left-0.5'
              }`} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
